import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { getCachedSchoolName, setCachedSchoolName } from '../utils/branding';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [schoolName, setSchoolName] = useState(getCachedSchoolName());
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/public/branding')
      .then((r) => {
        if (r.data?.school_name) {
          setCachedSchoolName(r.data.school_name);
          setSchoolName(r.data.school_name);
        }
      })
      .catch(() => {});
    const saved = localStorage.getItem('remembered_email');
    if (saved) setEmail(saved);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const trimmedEmail = email.trim().toLowerCase();

    // ─────────────────────────────────────
    // 1️⃣ MASTER ADMIN login (tried first)
    // ─────────────────────────────────────
    try {
      const form = new FormData();
      form.append('username', trimmedEmail);
      form.append('password', password);

      const res = await api.post('/super-admin/token', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Master admin succeeded → store master token separately
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('master_token', res.data.access_token);
      localStorage.setItem('is_super_admin', 'true');
      localStorage.setItem(
        'super_admin_name',
        `${res.data.user.first_name} ${res.data.user.last_name}`
      );
      localStorage.removeItem('active_school_id');
      localStorage.removeItem('active_school_name');

      if (remember) localStorage.setItem('remembered_email', trimmedEmail);
      else localStorage.removeItem('remembered_email');

      // Full reload so AuthContext picks up the new state
      window.location.href = '/select-school';
      return;
    } catch (masterErr) {
      if (masterErr.response?.status !== 401) {
        const detail = masterErr.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Something went wrong.');
        setLoading(false);
        return;
      }
      // 401 → not a master admin, fall through to school login
    }

    // ─────────────────────────────────────
    // 2️⃣ SCHOOL USER login (fallback)
    // ─────────────────────────────────────
    try {
      await login(trimmedEmail, password);

      // Clear any stale master state
      localStorage.removeItem('is_super_admin');
      localStorage.removeItem('super_admin_name');
      localStorage.removeItem('master_token');
      localStorage.removeItem('active_school_id');
      localStorage.removeItem('active_school_name');

      if (remember) localStorage.setItem('remembered_email', trimmedEmail);
      else localStorage.removeItem('remembered_email');

      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : 'Invalid email or password. Please try again.'
      );
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* LEFT PANEL */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/5 rounded-full"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-white/5 rounded-full"></div>

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/20">
              <i className="fas fa-graduation-cap text-2xl"></i>
            </div>
            <div>
              <p className="text-sm uppercase tracking-widest text-blue-100/80">Welcome to</p>
              <h1 className="text-2xl font-bold leading-tight">{schoolName}</h1>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h2 className="text-3xl font-bold leading-snug mb-4">
            Manage your entire school<br />from one place.
          </h2>
          <p className="text-blue-100/90 mb-8">
            Students, attendance, grades, fees, staff, salaries — everything your school needs.
          </p>
          <div className="space-y-3">
            {[
              { icon: 'fa-user-graduate', text: 'Student & Parent management' },
              { icon: 'fa-clipboard-check', text: 'Daily attendance tracking' },
              { icon: 'fa-credit-card', text: 'Fees, discounts & receipts' },
              { icon: 'fa-chart-line', text: 'Reports and analytics' },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <i className={`fas ${f.icon}`}></i>
                </div>
                <span className="text-blue-50/90">{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-blue-100/60">
          © {new Date().getFullYear()} {schoolName}. All rights reserved.
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <div className="w-14 h-14 rounded-xl bg-blue-600 text-white flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-graduation-cap text-2xl"></i>
            </div>
            <h1 className="text-xl font-bold text-gray-800">{schoolName}</h1>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Sign in</h2>
              <p className="text-sm text-gray-500 mt-1">Enter your credentials to continue</p>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                <i className="fas fa-exclamation-circle mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email address</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400">
                    <i className="fas fa-envelope text-sm"></i>
                  </span>
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder="Email ID"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400">
                    <i className="fas fa-lock text-sm"></i>
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="w-full pl-10 pr-11 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 select-none">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Remember me
                </label>
                <button
                  type="button"
                  onClick={() => alert('Ask your administrator to reset your password.')}
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><i className="fas fa-spinner fa-spin"></i> Signing in…</>
                ) : (
                  <><i className="fas fa-sign-in-alt"></i> Sign in</>
                )}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-400">
                <i className="fas fa-shield-alt mr-1"></i> Secured by SchoolSphere
              </p>
            </div>
          </div>

          <p className="text-xs text-gray-400 text-center mt-6">
            By signing in, you agree to keep student data confidential.
          </p>
        </div>
      </div>
    </div>
  );
}